import config from '../config/env';
import { safeStorage } from '../utils/safeStorage';

interface APIRequest {
  timestamp: string;
  endpoint: string;
  method: string;
  status: number;
  duration: number;
}

interface APIStats {
  totalRequests: number;
  ssoRequests: number;
  apiRequests: number;
  successfulRequests: number;
  failedRequests: number;
  requestHistory: APIRequest[];
}

class APIStatsService {
  private readonly MAX_HISTORY_SIZE = 100;
  private readonly STORAGE_KEY = 'api_stats';

  constructor() {
    this.initializeStats();
  }

  private initializeStats(): void {
    const stats = this.getStats();
    if (!stats.requestHistory) {
      this.resetStats();
    }
  }

  public getStats(): APIStats {
    const defaultStats: APIStats = {
      totalRequests: 0,
      ssoRequests: 0,
      apiRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      requestHistory: []
    };

    try {
      const stored = safeStorage.getItem(this.STORAGE_KEY);
      return stored ? { ...defaultStats, ...JSON.parse(stored) } : defaultStats;
    } catch (error) {
      console.warn('[API STATS] Error reading stats from storage:', error);
      return defaultStats;
    }
  }

  public recordRequest(
    endpoint: string,
    method: string,
    status: number,
    duration: number
  ): void {
    const stats = this.getStats();

    const request: APIRequest = {
      timestamp: new Date().toISOString(),
      endpoint: this.sanitizeEndpoint(endpoint),
      method: method.toUpperCase(),
      status,
      duration
    };

    // Update counters
    stats.totalRequests++;

    if (this.isSSO(endpoint)) {
      stats.ssoRequests++;
    } else {
      stats.apiRequests++;
    }

    if (status >= 200 && status < 400) {
      stats.successfulRequests++;
    } else {
      stats.failedRequests++;
    }

    // Add to history and maintain size limit
    stats.requestHistory.push(request);
    if (stats.requestHistory.length > this.MAX_HISTORY_SIZE) {
      stats.requestHistory = stats.requestHistory.slice(-this.MAX_HISTORY_SIZE);
    }

    this.saveStats(stats);
    // Per-request log removed: volume is proportional to traffic and the
    // same data is available in the StatisticsModal. Aggregate errors are
    // surfaced via the catch branch above.
  }

  public resetStats(): void {
    const defaultStats: APIStats = {
      totalRequests: 0,
      ssoRequests: 0,
      apiRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      requestHistory: []
    };

    this.saveStats(defaultStats);

    if (config.dev.debug) {
      console.log('[API STATS] Statistics reset');
    }
  }

  public getRequestsByEndpoint(): Record<string, number> {
    const stats = this.getStats();
    const endpointCounts: Record<string, number> = {};

    stats.requestHistory.forEach(request => {
      endpointCounts[request.endpoint] = (endpointCounts[request.endpoint] || 0) + 1;
    });

    return endpointCounts;
  }

  public getAverageResponseTime(): number {
    const stats = this.getStats();

    if (stats.requestHistory.length === 0) {
      return 0;
    }

    const totalDuration = stats.requestHistory.reduce((sum, request) => sum + request.duration, 0);
    return Math.round(totalDuration / stats.requestHistory.length);
  }

  public getErrorRate(): number {
    const stats = this.getStats();

    if (stats.totalRequests === 0) {
      return 0;
    }

    return Math.round((stats.failedRequests / stats.totalRequests) * 100);
  }

  public getRecentRequests(count: number = 10): APIRequest[] {
    const stats = this.getStats();
    return stats.requestHistory.slice(-count).reverse();
  }

  private saveStats(stats: APIStats): void {
    try {
      safeStorage.setItem(this.STORAGE_KEY, JSON.stringify(stats));
    } catch (error) {
      console.error('[API STATS] Error saving stats to storage:', error);
    }
  }

  private sanitizeEndpoint(endpoint: string): string {
    // Remove query parameters and sensitive info
    const url = endpoint.split('?')[0];

    // Replace IDs with placeholder for better grouping
    return url.replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '/:id')
             .replace(/\/[0-9]+/g, '/:id')
             .replace(/\/[a-zA-Z0-9]{20,}/g, '/:token');
  }

  private isSSO(endpoint: string): boolean {
    return endpoint.includes('sso.strong.blue') ||
           endpoint.includes('/oauth/') ||
           endpoint.includes('/auth/') ||
           endpoint.includes('openid-connect');
  }
}

// Export singleton instance
export const apiStatsService = new APIStatsService();

// Export types for use in components
export type { APIRequest, APIStats };